import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  Database,
  Users,
  HelpCircle,
  Headphones,
  Gift,
  ChevronRight,
} from 'lucide-react';

const features = [
  {
    icon: Bot,
    title: 'AI Chat Agent Setup',
    description: 'Create intelligent chat agents that understand context, handle complex queries, and learn from your business data. Customize tone, language, and behavior.',
    tag: 'Core',
    slug: '/features/ai-chat-agent',
  },
  {
    icon: Database,
    title: 'Knowledge Base',
    description: 'Upload documents, FAQs, product info, and pricing. Your agent references this knowledge instantly for accurate, brand-consistent responses.',
    tag: 'Core',
    slug: '/features/knowledge-base',
  },
  {
    icon: Users,
    title: 'Lead Capture',
    description: 'Automatically collect customer details, contact information, and preferences during conversations. Export leads to your CRM.',
    tag: 'Growth',
    slug: '/features/lead-capture',
  },
  {
    icon: HelpCircle,
    title: 'FAQ Automation',
    description: 'Handle repetitive questions automatically. Your AI agent resolves common queries instantly, freeing your team for complex issues.',
    tag: 'Automation',
    slug: '/features/faq-automation',
  },
  {
    icon: Headphones,
    title: 'Support Automation',
    description: 'Resolve customer support tickets on WhatsApp 24/7. Escalate complex cases to human agents when needed.',
    tag: 'Automation',
    slug: '/features/support-automation',
  },
  {
    icon: Gift,
    title: 'Trial Support',
    description: 'Full-featured 7-day free trial with onboarding support. No credit card required. Experience the full platform risk-free.',
    tag: 'Onboarding',
    slug: '/features/trial-support',
  },
];

export const Features = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const navigate = useNavigate();

  return (
    <section id="features" ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute top-1/2 left-0 w-[300px] h-[300px] rounded-full bg-primary/[0.03] blur-[80px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="premium" className="mb-4">
              Feature Highlights
            </Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight"
          >
            Packed with{' '}
            <span className="gradient-text-primary">Powerful Features</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed"
          >
            Everything you need to automate, engage, and grow — all from your WhatsApp.
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              className={`glass-card rounded-2xl p-6 cursor-default flex flex-col transition-colors duration-500 ${
                hoveredIdx === i
                  ? 'border-primary/20 shadow-glow-primary/50'
                  : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <feature.icon size={18} className="text-primary" />
                </div>
                <Badge variant="glass" className="text-[10px]">
                  {feature.tag}
                </Badge>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-body">
                {feature.description}
              </p>
              <div className="mt-auto pt-4">
                <button
                  onClick={() => navigate(feature.slug)}
                  className="inline-flex items-center gap-1 text-xs text-primary font-medium cursor-pointer hover:gap-2 transition-all duration-200 bg-transparent border-none p-0"
                >
                  Learn more <ChevronRight size={12} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
